import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import type { User, AuthState } from '@/types';
import { userPool } from '@/lib/cognito';

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => void;
  signUp: (email: string, password: string, name?: string) => Promise<void>;
  signOut: () => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  confirmSignUpAndSignIn: (email: string, code: string, password: string) => Promise<void>;
  resendConfirmationCode: (email: string) => Promise<void>;
  getIdToken: () => Promise<string | null>;
  handleOAuthCallback: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const checkAuth = () => {
      const clientId = import.meta.env.VITE_USER_POOL_CLIENT_ID;
      const lastAuthUser = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.LastAuthUser`);
      
      // Check if this is an OAuth user first
      if (lastAuthUser) {
        const isOAuthUser = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.isOAuth`) === 'true';
        
        if (isOAuthUser) {
          // For OAuth users, get user info from the stored ID token
          const idToken = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.idToken`);
          
          if (idToken) {
            try {
              const idTokenPayload = JSON.parse(atob(idToken.split('.')[1]));
              
              // Check if token is expired
              if (idTokenPayload.exp * 1000 < Date.now()) {
                // Token expired, clear auth state
                setIsAuthenticated(false);
                setUser(null);
                setIsLoading(false);
                return;
              }
              
              setUser({
                id: idTokenPayload.sub,
                email: idTokenPayload.email,
                name: idTokenPayload.name || idTokenPayload.email,
                createdAt: new Date().toISOString(),
              });
              setIsAuthenticated(true);
              setIsLoading(false);
              return;
            } catch (error) {
              console.error('Error parsing OAuth token:', error);
            }
          }
          
          // OAuth token invalid or missing
          setIsAuthenticated(false);
          setUser(null);
          setIsLoading(false);
          return;
        }
      }
      
      // For email/password users, use the Cognito SDK
      const cognitoUser = userPool.getCurrentUser();
      
      if (cognitoUser) {
        cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
          if (err || !session || !session.isValid()) {
            setIsAuthenticated(false);
            setUser(null);
            setIsLoading(false);
            return;
          }

          cognitoUser.getUserAttributes((err, attributes) => {
            if (err) {
              setIsAuthenticated(false);
              setUser(null);
              setIsLoading(false);
              return;
            }

            const email = attributes?.find(attr => attr.Name === 'email')?.Value || '';
            const name = attributes?.find(attr => attr.Name === 'name')?.Value || email;

            setUser({
              id: cognitoUser.getUsername(),
              email,
              name,
              createdAt: new Date().toISOString(),
            });
            setIsAuthenticated(true);
            setIsLoading(false);
          });
        });
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      return new Promise<void>((resolve, reject) => {
        const authenticationDetails = new AuthenticationDetails({
          Username: email,
          Password: password,
        });

        const cognitoUser = new CognitoUser({
          Username: email,
          Pool: userPool,
        });

        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: () => {
            cognitoUser.getUserAttributes((err, attributes) => {
              if (err) {
                reject(err);
                return;
              }

              const name = attributes?.find(attr => attr.Name === 'name')?.Value || email;

              setUser({
                id: cognitoUser.getUsername(),
                email,
                name,
                createdAt: new Date().toISOString(),
              });
              setIsAuthenticated(true);
              resolve();
            });
          },
          onFailure: (err) => {
            // Check if user is not confirmed
            if (err.code === 'UserNotConfirmedException') {
              const notConfirmedError = new Error('USER_NOT_CONFIRMED') as Error & { email?: string };
              notConfirmedError.email = email;
              reject(notConfirmedError);
            } else {
              reject(err);
            }
          },
        });
      });
    },
    []
  );

  const signUp = useCallback(
    async (email: string, password: string, name?: string) => {
      return new Promise<void>((resolve, reject) => {
        const attributeList = [
          new CognitoUserAttribute({ Name: 'email', Value: email }),
        ];

        if (name) {
          attributeList.push(new CognitoUserAttribute({ Name: 'name', Value: name }));
        }

        userPool.signUp(email, password, attributeList, [], (err) => {
          if (err) {
            reject(err);
            return;
          }
          resolve();
        });
      });
    },
    []
  );

  const signOut = useCallback(async () => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const confirmSignUp = useCallback(
    async (email: string, code: string) => {
      return new Promise<void>((resolve, reject) => {
        const cognitoUser = new CognitoUser({
          Username: email,
          Pool: userPool,
        });

        cognitoUser.confirmRegistration(code, true, (err) => {
          if (err) {
            reject(err);
            return;
          }
          resolve();
        });
      });
    },
    []
  );

  const confirmSignUpAndSignIn = useCallback(
    async (email: string, code: string, password: string) => {
      // First confirm the signup
      await confirmSignUp(email, code);
      
      try {
        // Then automatically sign in the user
        await signIn(email, password);
      } catch (error) {
        // If sign-in fails after successful verification, provide clearer error message
        const signInError = new Error('Your email has been verified, but automatic sign-in failed. Please try signing in manually.');
        throw signInError;
      }
    },
    [confirmSignUp, signIn]
  );

  const resendConfirmationCode = useCallback(
    async (email: string) => {
      return new Promise<void>((resolve, reject) => {
        const cognitoUser = new CognitoUser({
          Username: email,
          Pool: userPool,
        });

        cognitoUser.resendConfirmationCode((err) => {
          if (err) {
            reject(err);
            return;
          }
          resolve();
        });
      });
    },
    []
  );

  const getIdToken = useCallback(async () => {
    return new Promise<string | null>((resolve) => {
      const clientId = import.meta.env.VITE_USER_POOL_CLIENT_ID;
      const lastAuthUser = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.LastAuthUser`);
      
      if (!lastAuthUser) {
        resolve(null);
        return;
      }

      // Check if this is an OAuth user
      const isOAuthUser = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.isOAuth`) === 'true';
      
      if (isOAuthUser) {
        // For OAuth users, get the token directly from localStorage
        const idToken = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.idToken`);
        
        if (!idToken) {
          resolve(null);
          return;
        }

        try {
          // Check if token is expired
          const idTokenPayload = JSON.parse(atob(idToken.split('.')[1]));
          if (idTokenPayload.exp * 1000 < Date.now()) {
            resolve(null);
            return;
          }
          resolve(idToken);
        } catch (error) {
          resolve(null);
        }
      } else {
        // For email/password users, use the Cognito SDK
        const cognitoUser = userPool.getCurrentUser();
        
        if (!cognitoUser) {
          resolve(null);
          return;
        }

        cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
          if (err || !session || !session.isValid()) {
            resolve(null);
            return;
          }
          resolve(session.getIdToken().getJwtToken());
        });
      }
    });
  }, []);

  const signInWithGoogle = useCallback(() => {
    const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;
    const clientId = import.meta.env.VITE_USER_POOL_CLIENT_ID;
    const redirectUri = window.location.origin;
    
    // Note: Remove &prompt=select_account for silent sign-in when already authorized with Google
    const oauthUrl = `https://${cognitoDomain}/oauth2/authorize?` +
      `identity_provider=Google&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=token&` +
      `client_id=${clientId}&` +
      `scope=email+openid+profile+aws.cognito.signin.user.admin`;
    
    window.location.href = oauthUrl;
  }, []);

  const handleOAuthCallback = useCallback(async () => {
    // Check for tokens in URL hash (implicit flow)
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);
    const idToken = params.get('id_token');
    const accessToken = params.get('access_token');
    
    if (!idToken || !accessToken) {
      return;
    }

    try {
      const clientId = import.meta.env.VITE_USER_POOL_CLIENT_ID;
      
      // Decode ID token to get user info
      const idTokenPayload = JSON.parse(atob(idToken.split('.')[1]));
      
      // Set user state first
      const userData = {
        id: idTokenPayload.sub,
        email: idTokenPayload.email,
        name: idTokenPayload.name || idTokenPayload.email,
        createdAt: new Date().toISOString(),
      };
      
      setUser(userData);
      setIsAuthenticated(true);

      // Store tokens in localStorage for the Cognito SDK
      const username = idTokenPayload['cognito:username'] || idTokenPayload.sub;
      
      localStorage.setItem(
        `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`,
        username
      );
      localStorage.setItem(
        `CognitoIdentityServiceProvider.${clientId}.${username}.idToken`,
        idToken
      );
      localStorage.setItem(
        `CognitoIdentityServiceProvider.${clientId}.${username}.accessToken`,
        accessToken
      );
      
      // Store clock drift
      localStorage.setItem(
        `CognitoIdentityServiceProvider.${clientId}.${username}.clockDrift`,
        '0'
      );
      
      // Mark this as an OAuth user so we don't try to use Cognito SDK methods
      localStorage.setItem(
        `CognitoIdentityServiceProvider.${clientId}.${username}.isOAuth`,
        'true'
      );

      // Clear the hash from URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Force a page reload to reinitialize everything with the new tokens
      window.location.reload();
    } catch (error) {
      console.error('OAuth callback error:', error);
      throw error;
    }
  }, []);

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    signIn,
    signInWithGoogle,
    signUp,
    signOut,
    confirmSignUp,
    confirmSignUpAndSignIn,
    resendConfirmationCode,
    getIdToken,
    handleOAuthCallback,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
